import React from 'react';
import classnames from 'classnames';
import { Classes } from '@blueprintjs/core';
import styles from './IconedRadioGroup.module.scss';

// Blueprint 5 не отдаёт isElementOfType наружу — проверка та же самая:
// сравнение типа элемента с компонентом
const isIconedRadio = (child: React.ReactNode): child is React.ReactElement<IconedRadioProps> =>
    React.isValidElement(child) && child.type === IconedRadio;

export const IconedRadio: React.FC<IconedRadioProps> = (props) => {
    const { inline, name, value, checked, label, onChange, children } = props;

    const className = classnames({
        'bp6-control bp6-radio': true,
        'bp6-inline': !!inline
    });

    // Разметка здесь написана руками, поэтому класс на input приходится ставить самим.
    // До Blueprint 6 нативный кружок прятало правило `.bp5-control input`, и класс был
    // не нужен; в шестой версии правило сузилось до `.bp6-control .bp6-control-input`,
    // и без класса поверх нашей иконки рисовался бы настоящий радиопереключатель.
    // Берём константу, а не строку: на следующем мажоре префикс сменится сам.
    return (
        <label className={className}>
            <input
                className={Classes.CONTROL_INPUT}
                name={name}
                type="radio"
                value={value}
                checked={checked}
                onChange={() => onChange && onChange(value || '')}
            />
            <span className={`${styles.indicator} control-indicator`} title={label}>
                {children}
            </span>
        </label>
    );
};

(IconedRadio as any).displayName = 'IconedRadio';

export const IconedRadioGroup: React.FC<IconedRadioGroupProps> = (props) => {
    const getRadioProps = (optionProps: IconedRadioProps) => {
        const { name, onChange, inline } = props;
        const { className, disabled, value, label } = optionProps;

        return {
            label,
            checked: value === props.selectedValue,
            className,
            disabled: disabled || props.disabled,
            inline,
            name,
            onChange,
            value
        };
    }

    const renderChildren = () => {
        return React.Children.map(props.children, (child) => {
            if (isIconedRadio(child)) {
                return React.cloneElement(child, getRadioProps(child.props));
            }
            else {
                return child;
            }
        });
    }

    return (
        <div className={`${styles.radio} ${props.className || ''}`}>
            {renderChildren()}
        </div>
    );
};

interface IconedRadioGroupProps {
    name: string;
    onChange?: (value: string) => void;
    selectedValue?: string;
    inline?: boolean;
    disabled?: boolean;
    className?: string;
    children?: React.ReactNode;
}

interface IconedRadioProps {
    label: string;
    name?: string;
    value?: string;
    checked?: boolean;
    disabled?: boolean;
    className?: string;
    inline?: boolean;
    onChange?: (value: string) => void;
    children?: React.ReactNode;
}
